import { Alert } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import MoneyOffOutlinedIcon from '@mui/icons-material/MoneyOffOutlined'
import { SvgIconComponent } from '@mui/icons-material'

type StatusType =
    | 'initial'
    | 'pending approval'
    | 'approved for invoice'
    | 'invoice generated'
    | 'invoice collected'
    | 'dispute'
    | 'invoice not required'
    | 'partially collected'
    | 'reported as collected'
    | 'bad debts'
    | string

interface StatusConfig {
    severity: 'info' | 'success' | 'warning' | 'error'
    icon: SvgIconComponent
    color: string
    borderColor: string
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
    initial: {
        severity: 'info',
        icon: FiberManualRecordIcon,
        color: '#533A71',
        borderColor: 'rgba(83, 58, 113, 0.5)',
    },
    'pending approval': {
        severity: 'warning',
        icon: HourglassEmptyIcon,
        color: '#E26D5A',
        borderColor: 'rgba(226, 109, 90, 0.5)',
    },
    'approved for invoice': {
        severity: 'success',
        icon: CheckCircleOutlineIcon,
        color: '#F0F465',
        borderColor: 'rgba(240, 244, 101, 0.5)',
    },
    'invoice generated': {
        severity: 'success',
        icon: DescriptionOutlinedIcon,
        color: '#50C5B7',
        borderColor: 'rgba(80, 197, 183, 0.5)',
    },
    'invoice collected': {
        severity: 'success',
        icon: PaidOutlinedIcon,
        color: '#FF9F40',
        borderColor: 'rgba(255, 159, 64, 0.5)',
    },
    dispute: {
        severity: 'error',
        icon: ReportProblemOutlinedIcon,
        color: '#9CEC5B',
        borderColor: 'rgba(156, 236, 91, 0.5)',
    },
    'invoice not required': {
        severity: 'info',
        icon: BlockOutlinedIcon,
        color: '#6184D8',
        borderColor: 'rgba(97, 132, 216, 0.5)',
    },
    'partially collected': {
        severity: 'warning',
        icon: AccountBalanceWalletOutlinedIcon,
        color: '#FF9F40',
        borderColor: 'rgba(255, 159, 64, 0.5)',
    },
    'reported as collected': {
        severity: 'success',
        icon: AssignmentTurnedInOutlinedIcon,
        color: '#FF9F40',
        borderColor: 'rgba(255, 159, 64, 0.5)',
    },
    'bad debts': {
        severity: 'error',
        icon: MoneyOffOutlinedIcon,
        color: '#9CEC5B',
        borderColor: 'rgba(156, 236, 91, 0.5)',
    },
}

const DEFAULT_CONFIG: StatusConfig = {
    severity: 'info',
    icon: PendingOutlinedIcon,
    color: '#757575',
    borderColor: 'rgba(117, 117, 117, 0.5)',
}

interface FMEStatusProps {
    status: StatusType
}

export default function FMEStatus({ status }: FMEStatusProps) {
    const config = STATUS_CONFIG[status] || DEFAULT_CONFIG
    const IconComponent = config.icon

    return (
        <div className="AlertItems">
            <Alert
                icon={<IconComponent />}
                severity={config.severity}
                sx={{
                    border: `1px solid ${config.borderColor}`,
                    padding: '0px 5px',
                    display: 'inline-flex',
                    backgroundColor: `${config.color}15`,
                    '& .MuiAlert-icon': {
                        color: config.color,
                    },
                    '& .MuiAlert-message': {
                        color: config.color,
                        fontWeight: 500,
                    },
                }}
            >
                {status
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ') || ''}
            </Alert>
        </div>
    )
}
